/**
 * useAddressLookup Hook
 * Custom hook for managing address lookup state and operations
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import addressService from '../services/addressService';
import { 
  UI_STATES, 
  SEARCH_CONFIG, 
  ERROR_MESSAGES, 
  SUCCESS_MESSAGES,
  VALIDATION
} from '../utils/addressConstants';

/**
 * Custom hook for address lookup functionality
 * @param {Object} options - Configuration options
 * @param {Function} options.onAddressSelect - Callback when address is selected
 * @param {Function} options.onError - Callback when error occurs
 * @param {boolean} options.autoSelectSingle - Auto-select if only one result
 * @param {number} options.debounceDelay - Debounce delay for search input
 * @param {Object} options.initialAddress - Initial address to display in selected state
 * @returns {Object} Hook state and methods
 */
export const useAddressLookup = (options = {}) => {
  const {
    onAddressSelect,
    onError,
    autoSelectSingle = true,
    debounceDelay = SEARCH_CONFIG.DEBOUNCE_DELAY,
    maxResults = 500,
    initialAddress = {}
  } = options;

  // Check if we have a valid initial address
  const hasInitialAddress = !!(
    initialAddress.address_line1 || 
    initialAddress.address_line2 || 
    initialAddress.address_city || 
    initialAddress.address_county || 
    initialAddress.address_postcode
  );

  // State
  const [uiState, setUiState] = useState(hasInitialAddress ? UI_STATES.SELECTED : UI_STATES.IDLE);
  const [postcode, setPostcode] = useState('');
  const [query, setQuery] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(
    hasInitialAddress ? {
      line1: initialAddress.address_line1 || '',
      line2: initialAddress.address_line2 || '',
      line3: '',
      city: initialAddress.address_city || '',
      county: initialAddress.address_county || '',
      postcode: initialAddress.address_postcode || '',
      country: initialAddress.address_country || 'United Kingdom',
      uprn: initialAddress.address_uprn || ''
    } : null
  );
  const [previousSelectedAddress, setPreviousSelectedAddress] = useState(null); // Store previous address for cancel
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  // Refs for debouncing
  const debounceTimeout = useRef(null);
  const abortController = useRef(null);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
    setValidationMessage('');
  }, []);

  /**
   * Reset component to initial state
   */
  const reset = useCallback(() => {
    setUiState(UI_STATES.IDLE);
    setPostcode('');
    setQuery('');
    setAddresses([]);
    setSelectedAddress(null);
    setError(null);
    setIsLoading(false);
    setValidationMessage('');
    
    // Cancel any ongoing requests
    if (abortController.current) {
      abortController.current.abort();
    }
    
    // Clear debounce timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
  }, []);

  /**
   * Validate postcode format
   */
  const validatePostcode = useCallback((postcodeValue) => {
    // Don't show validation error for empty postcode - that's handled elsewhere
    if (!postcodeValue.trim()) {
      setValidationMessage('');
      return false;
    }

    if (!VALIDATION.UK_POSTCODE.test(postcodeValue.trim())) {
      setValidationMessage(ERROR_MESSAGES.INVALID_POSTCODE);
      return false;
    }

    setValidationMessage('');
    return true;
  }, []);

  /**
   * Handle API errors
   */
  const handleError = useCallback((errorMessage) => {
    setError(errorMessage);
    setIsLoading(false);
    setUiState(UI_STATES.ERROR);
    
    if (onError) {
      onError(errorMessage);
    }
  }, [onError]);

  /**
   * Perform address search
   */
  const searchAddresses = useCallback(async (postcodeValue, queryValue = '') => {
    // Cancel previous request
    if (abortController.current) {
      abortController.current.abort();
    }

    // Check for missing postcode - show gentle notification instead of error
    if (!postcodeValue.trim()) {
      handleError('Please enter a postcode to search for addresses');
      return;
    }

    // Validate postcode format
    if (!validatePostcode(postcodeValue)) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setUiState(UI_STATES.SEARCHING);
    
    // Create new abort controller for this request
    abortController.current = new AbortController();

    try {
      let result;
      
      if (queryValue.trim()) {
        // Search with query
        result = await addressService.searchByQuery(
          postcodeValue, 
          queryValue,
          { limit: maxResults }
        );
      } else {
        // Search by postcode only
        result = await addressService.searchByPostcode(
          postcodeValue,
          { limit: maxResults }
        );
      }

      if (result.status === 'error') {
        handleError(result.error || ERROR_MESSAGES.NO_RESULTS);
        return;
      }

      const foundAddresses = result.addresses || [];
      setAddresses(foundAddresses);
      setIsLoading(false);

      if (foundAddresses.length === 0) {
        setError(ERROR_MESSAGES.NO_RESULTS);
        setUiState(UI_STATES.ERROR);
      } else if (foundAddresses.length === 1 && autoSelectSingle) {
        // Auto-select single result
        handleAddressSelect(foundAddresses[0]);
      } else {
        setUiState(UI_STATES.RESULTS);
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        return; // Request was cancelled
      }

      let errorMessage = ERROR_MESSAGES.GENERIC_ERROR;
      
      if (error.message.includes('rate limit') || error.message.includes('429')) {
        errorMessage = ERROR_MESSAGES.RATE_LIMITED;
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = ERROR_MESSAGES.NETWORK_ERROR;
      } else if (error.message.includes('503')) {
        errorMessage = ERROR_MESSAGES.SERVICE_UNAVAILABLE;
      }

      handleError(errorMessage);
    }
  }, [validatePostcode, handleError, autoSelectSingle]);

  /**
   * Debounced search function
   */
  const debouncedSearch = useCallback((postcodeValue, queryValue) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      searchAddresses(postcodeValue, queryValue);
    }, debounceDelay);
  }, [searchAddresses, debounceDelay]);

  /**
   * Handle postcode input change
   */
  const handlePostcodeChange = useCallback((value) => {
    setPostcode(value);
    clearError();
    
    if (value.length >= SEARCH_CONFIG.MIN_POSTCODE_LENGTH) {
      debouncedSearch(value, query);
    } else {
      setUiState(UI_STATES.IDLE);
      setAddresses([]);
    }
  }, [query, debouncedSearch, clearError]);

  /**
   * Handle query input change
   */
  const handleQueryChange = useCallback((value) => {
    setQuery(value);
    clearError();
    
    if (postcode.length >= SEARCH_CONFIG.MIN_POSTCODE_LENGTH) {
      debouncedSearch(postcode, value);
    }
  }, [postcode, debouncedSearch, clearError]);

  /**
   * Handle address selection
   */
  const handleAddressSelect = useCallback((address) => {
    setSelectedAddress(address);
    setUiState(UI_STATES.SELECTED);
    setError(null);
    
    // Convert to form fields format
    const formFields = addressService.convertToFormFields(address);
    
    if (onAddressSelect) {
      onAddressSelect(formFields, address);
    }
  }, [onAddressSelect]);

  /**
   * Switch to manual address entry
   */
  const switchToManualEntry = useCallback(() => {
    // Save current selected address before switching
    if (selectedAddress) {
      setPreviousSelectedAddress(selectedAddress);
    }
    setUiState(UI_STATES.MANUAL);
    setError(null);
  }, [selectedAddress]);

  /**
   * Switch back to address search
   */
  const switchToAddressSearch = useCallback(() => {
    // Save current selected address before switching
    if (selectedAddress) {
      setPreviousSelectedAddress(selectedAddress);
    }
    setUiState(UI_STATES.IDLE);
    setError(null);
    setQuery(''); // Clear the building name/number field
    setSelectedAddress(null);
  }, [selectedAddress]);

  /**
   * Cancel and restore previous selected address
   */
  const cancelAndRestoreAddress = useCallback(() => {
    if (previousSelectedAddress) {
      setSelectedAddress(previousSelectedAddress);
      setUiState(UI_STATES.SELECTED);
      setError(null);
      setPreviousSelectedAddress(null);

      // Convert to form fields format and notify parent
      const formFields = addressService.convertToFormFields(previousSelectedAddress);
      if (onAddressSelect) {
        onAddressSelect(formFields, previousSelectedAddress);
      }
    } else {
      // No previous address, just reset
      setUiState(UI_STATES.IDLE);
      setError(null);
    }
  }, [previousSelectedAddress, onAddressSelect]);

  /**
   * Watch for late-arriving initialAddress changes (e.g. from parent useEffect)
   * Only transition to SELECTED if we're still in IDLE and address becomes populated
   */
  useEffect(() => {
    const nowHasAddress = !!(
      initialAddress.address_line1 ||
      initialAddress.address_line2 ||
      initialAddress.address_city ||
      initialAddress.address_county ||
      initialAddress.address_postcode
    );

    if (nowHasAddress && uiState === UI_STATES.IDLE && !selectedAddress) {
      setSelectedAddress({
        line1: initialAddress.address_line1 || '',
        line2: initialAddress.address_line2 || '',
        line3: '',
        city: initialAddress.address_city || '',
        county: initialAddress.address_county || '',
        postcode: initialAddress.address_postcode || '',
        country: initialAddress.address_country || 'United Kingdom',
        uprn: initialAddress.address_uprn || ''
      });
      setUiState(UI_STATES.SELECTED);
    }
  }, [
    initialAddress.address_line1,
    initialAddress.address_line2,
    initialAddress.address_city,
    initialAddress.address_county,
    initialAddress.address_postcode,
    initialAddress.address_country
  ]);

  /**
   * Cleanup effect
   */
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  return {
    // State
    uiState,
    postcode,
    query,
    addresses,
    selectedAddress,
    previousSelectedAddress,
    error,
    isLoading,
    validationMessage,

    // Actions
    handlePostcodeChange,
    handleQueryChange,
    handleAddressSelect,
    searchAddresses,
    switchToManualEntry,
    switchToAddressSearch,
    cancelAndRestoreAddress,
    reset,
    clearError,

    // Utils
    formatAddress: addressService.formatAddressForDisplay,
    convertToFormFields: addressService.convertToFormFields
  };
};

export default useAddressLookup;